import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import _ from 'lodash';

import { IconButton } from '../common/IconButton'; // Assuming IconButton.js is in the same folder or adjust path

const MIN_ZOOM = 0.2; // Allow zooming out a bit more
const MAX_ZOOM = 20; // Allow zooming in more
const ZOOM_SENSITIVITY = 0.001;
const BASE_HANDLE_SIZE = 8; // Visual size of handles in screen pixels at zoom=1
const BASE_POINT_RADIUS = 5; // Visual size of point annotations in screen pixels at zoom=1
const BASE_STROKE_WIDTH = 1.5; // Stroke width in screen pixels at zoom=1

// --- Helper Functions ---

const clamp = (value, min, max) => Math.max(min, Math.min(value, max));

// Generate a simple unique ID (replace with a more robust method like UUID if needed)
const generateId = () => `ann_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`;

// --- Component ---

function ImageAnnotationsEditor({ value, onChange, knownAnnotations = null, onImageLoad = null, initialWidth = null, initialHeight = null }) {
    const { url, annotations = [] } = value || { url: null, annotations: [] };

    knownAnnotations =  knownAnnotations || [
        // {type: 'boundingBox', id: 'carBox', color: '#0088CC'},
        {type: 'point', id: 'wheelFront', color: '#FF00FF'},
        {type: 'point', id: 'wheelRear', color: '#FF00FF'},
    ]
    
    const [imgSize, setImgSize] = useState(initialWidth && initialHeight ? {width: initialWidth, height: initialHeight} : null);
    const [containerSize, setContainerSize] = useState(null); // Size of the outer div
    const [zoom, setZoom] = useState(1);
    // Pan now represents the translation in CSS pixels of the top-left corner of the scaled content
    const [pan, setPan] = useState({ x: 0, y: 0 });
    const [selectedAnnotationId, setSelectedAnnotationId] = useState(null);
    const [interaction, setInteraction] = useState(null); // { type: 'pan', 'drag_point', ..., startMouse, startPan, startAnn, startCoords, currentCoords }
    const [interactionMode, setInteractionMode] = useState('select'); // 'select', 'add_point', 'add_box'
    const [nextId, setNextId] = useState("");

    const containerRef = useRef(null);
    const transformWrapperRef = useRef(null); // Ref for the div applying transform
    const imgRef = useRef(null);

    const computeMinZoom = () => {
        const containerRatio = containerSize.width / containerSize.height;
        const imageRatio = imgSize.width / imgSize.height;
        const newZoom = containerRatio > imageRatio ? (containerSize.height * 0.95) / imgSize.height : (containerSize.width * 0.95) / imgSize.width;
        return newZoom;
    };

    const resetZoom = () => {
        if (!containerSize.width || !containerSize.height || !imgSize.width || !imgSize.height) return;
                
        // Calculate zoom level to fit image at 95% of container size
        const newZoom = computeMinZoom();
        
        // Calculate pan to center the image
        const scaledWidth = imgSize.width * newZoom;
        const scaledHeight = imgSize.height * newZoom;
        const newPanX = (containerSize.width - scaledWidth) / 2;
        const newPanY = (containerSize.height - scaledHeight) / 2;
        
        setZoom(newZoom);
        setPan({ x: newPanX, y: newPanY });
    };

    // --- Effects ---

    // Update container size for calculations
    useEffect(() => {
        const updateSize = () => {
            if (containerRef.current) {
                const rect = containerRef.current.getBoundingClientRect();
                setContainerSize({ width: rect.width, height: rect.height });
            }
        };
        updateSize();
        window.addEventListener('resize', updateSize);
        return () => window.removeEventListener('resize', updateSize);
    }, []);


    const handleLoad = () => {
        const img = imgRef.current;

        setImgSize({
            width: img.naturalWidth,
            height: img.naturalHeight
        });        

        onImageLoad && onImageLoad(img);
    };

    // Load image dimensions and reset view on URL change
    // useEffect(() => {
    //     console.log('useEffect url', url);
    //     setImgSize(null);
    //     setSelectedAnnotationId(null);
    //     setInteraction(null);
    //     setInteractionMode('select');
    // }, [url]); // Rerun when URL changes

    useEffect(() => {
        imgSize && containerSize && resetZoom();
    }, [JSON.stringify(imgSize), JSON.stringify(containerSize)]);


    // --- Coordinate Conversion ---

    // Convert screen coordinates (e.g., mouse event clientX/Y) to relative (0-1) image coordinates
    const screenToRelativeCoords = useCallback((screenX, screenY) => {
        if (!containerRef.current || !imgSize || !transformWrapperRef.current) {
            return { x: 0, y: 0 };
        }
        const contRect = containerRef.current.getBoundingClientRect();
        // Mouse position relative to the container's top-left
        const mouseX = screenX - contRect.left;
        const mouseY = screenY - contRect.top;

        // Account for the CSS transform (pan and zoom) applied to the wrapper
        // Calculate mouse position relative to the *unscaled, untranslated* image content's top-left corner
        const imageX = (mouseX - pan.x) / zoom;
        const imageY = (mouseY - pan.y) / zoom;

        // Convert to relative coordinates (0-1) based on the *original* image dimensions
        const relativeX = imageX / imgSize.width;
        const relativeY = imageY / imgSize.height;

        return { x: relativeX, y: relativeY };

    }, [pan, zoom, imgSize?.width, imgSize?.height]);

    // Convert relative (0-1) image coordinates to SVG coordinates (pixels within original image)
    // This remains unchanged as SVG coordinates map to original image pixels
    const relativeToSvgCoords = (relX, relY) => {
        if (!imgSize || !imgSize.width || !imgSize.height) return { x: 0, y: 0 };
        return {
            x: relX * imgSize.width,
            y: relY * imgSize.height,
        };
    };

    // --- Interaction Logic ---

    const updateAnnotation = useCallback((id, changes) => {
        const newAnnotations = annotations.map(ann =>
            ann.id === id ? { ...ann, ...changes } : ann
        );
        onChange({ ...value, annotations: newAnnotations });
    }, [annotations, onChange, value]);

    const addAnnotation = useCallback((newAnnotation) => {
         const newAnnotations = [...annotations, newAnnotation];
         onChange({ ...value, annotations: newAnnotations });
    }, [annotations, onChange, value]);

    const deleteAnnotation = useCallback((id) => {
        const newAnnotations = annotations.filter(a => a.id !== id);
        onChange({ ...value, annotations: newAnnotations });
        setSelectedAnnotationId(null); // Deselect after deleting
    }, [annotations, onChange, value]);


    const handleMouseDown = useCallback((e) => {
        if (!imgSize.width || e.button !== 0) return; // Only left clicks
        containerRef.current?.focus();
        const startMouse = { x: e.clientX, y: e.clientY };
        const startRelativeCoords = screenToRelativeCoords(e.clientX, e.clientY);
        const startSvgCoords = relativeToSvgCoords(startRelativeCoords.x, startRelativeCoords.y);

        // Handle Adding New Annotations
        if (interactionMode === 'add_point') {
            let newId = nextId;
            if(!newId) {
                newId = prompt('Enter an ID for the new point', generateId());
            }
            addAnnotation({
                id: newId,
                type: 'point',
                ...startRelativeCoords
            });
            setInteractionMode('select'); // Switch back to select mode after adding
            return; // Don't process further for selection/drag
        }
        if (interactionMode === 'add_box') {
             setInteraction({
                type: 'drawing_box',
                startMouse,
                startCoords: startRelativeCoords, // Store relative coords
                currentCoords: startRelativeCoords, // Initialize current same as start
            });
            return; // Start drawing interaction
        }

        // Handle Selection, Dragging, Resizing (only in 'select' mode)
        if (interactionMode === 'select') {
            const target = e.target;
            const annotationId = target?.getAttribute('data-annotation-id');
            const handleType = target?.getAttribute('data-handle-type');

            console.log('handleMouseDown', annotationId, handleType, target);

            if (annotationId) {
                const ann = annotations.find(a => a.id === annotationId);
                if (!ann) return;
        
                setSelectedAnnotationId(annotationId);

                // Calculate SVG coordinates for offsets/resizing based on *original* image pixels
                const annSvgCoords = ann.type === 'point'
                    ? relativeToSvgCoords(ann.x, ann.y)
                    : relativeToSvgCoords(ann.left, ann.top);

                if (handleType) { // Resize Box
                    setInteraction({
                        type: 'resize_box',
                        id: annotationId,
                        handle: handleType,
                        startMouse,
                        startAnn: { ...ann }
                    });
                } else if (ann.type === 'boundingBox') { // Drag Box
                    setInteraction({
                        type: 'drag_box',
                        id: annotationId,
                        startMouse,
                        startAnn: { ...ann },
                        offset: { // Offset in SVG coords
                            x: startSvgCoords.x - annSvgCoords.x,
                            y: startSvgCoords.y - annSvgCoords.y,
                        }
                    });
                } else if (ann.type === 'point') { // Drag Point
                    setInteraction({
                        type: 'drag_point',
                        id: annotationId,
                        startMouse,
                        startAnn: { ...ann },
                        offset: { // Offset in SVG coords
                             x: startSvgCoords.x - annSvgCoords.x,
                             y: startSvgCoords.y - annSvgCoords.y,
                        }
                    });
                }
            } else {
                // Clicked on background - initiate pan
                setSelectedAnnotationId(null);
                setInteraction({
                    type: 'pan',
                    startMouse,
                    startPan: { ...pan }
                });
            }
        }
    }, [imgSize?.width, screenToRelativeCoords, interactionMode, annotations, pan, addAnnotation, relativeToSvgCoords]);

    const handleMouseMove = useCallback((e) => {
        if (!interaction || !imgSize?.width) return;

        const currentMouse = { x: e.clientX, y: e.clientY };
        const currentRelativeCoords = screenToRelativeCoords(currentMouse.x, currentMouse.y);
        const currentSvgCoords = relativeToSvgCoords(currentRelativeCoords.x, currentRelativeCoords.y);
        // No need for startSvgCoords generally, work with relative or direct SVG coords as needed

        switch (interaction.type) {
             case 'pan': {
                // Pan amount is direct screen pixel difference
                const deltaX = currentMouse.x - interaction.startMouse.x;
                const deltaY = currentMouse.y - interaction.startMouse.y;

                // Calculate new pan, clamping might need refinement based on desired behavior when zoomed out < container
                let newPanX = interaction.startPan.x + deltaX;
                let newPanY = interaction.startPan.y + deltaY;

                setPan({ x: newPanX, y: newPanY });
                break;
            }
             case 'drag_point': {
                const { offset } = interaction;
                // Calculate the target SVG position by applying the offset to the current mouse SVG position
                const targetSvgX = currentSvgCoords.x - offset.x;
                const targetSvgY = currentSvgCoords.y - offset.y;
                // Convert back to relative coordinates for storage
                const newRelative = {
                     x: targetSvgX / imgSize.width,
                     y: targetSvgY / imgSize.height
                 };
                updateAnnotation(interaction.id, newRelative);
                break;
            }
             case 'drag_box': {
                 const { startAnn, offset } = interaction;
                 // Calculate the target top-left SVG position
                 const targetSvgX = currentSvgCoords.x - offset.x;
                 const targetSvgY = currentSvgCoords.y - offset.y;

                 // Convert the target SVG top-left to relative coordinates
                 let newRelX = targetSvgX / imgSize.width;
                 let newRelY = targetSvgY / imgSize.height;

                 // Clamp to prevent dragging the box entirely out of bounds (top-left corner)
                //  newRelX = clamp(newRelX, 0, 1 - startAnn.width);
                //  newRelY = clamp(newRelY, 0, 1 - startAnn.height);

                 updateAnnotation(interaction.id, { left: newRelX, top: newRelY });
                 break;
             }
             case 'resize_box': {
                const { startAnn, handle } = interaction;
                // Use startAnn for original relative dimensions, convert mouse pos directly
                const currentRel = currentRelativeCoords;

                let { left, top, width, height } = startAnn;
                let right = left + width;
                let bottom = top + height;

                // Adjust edges based on handle type and current relative mouse position
                if (handle.includes('l')) left = currentRel.x;
                if (handle.includes('r')) right = currentRel.x;
                if (handle.includes('t')) top = currentRel.y;
                if (handle.includes('b')) bottom = currentRel.y;

                // Ensure width/height are positive (swap edges if needed)
                const newLeft = Math.min(left, right);
                const newTop = Math.min(top, bottom);
                const newRight = Math.max(left, right);
                const newBottom = Math.max(top, bottom);

                const newAnnotation = {
                    ...startAnn,
                    left: newLeft,
                    top: newTop,
                    width: newRight - newLeft,
                    height: newBottom - newTop,
                };
                updateAnnotation(interaction.id, newAnnotation);
                break;
            }
            case 'drawing_box': {
                // Update the temporary end coordinates for visual feedback
                setInteraction(prev => ({ ...prev, currentCoords: currentRelativeCoords }));
                break;
            }
            default:
              break;
        }

    }, [interaction, screenToRelativeCoords, imgSize, zoom, containerSize, pan, updateAnnotation, relativeToSvgCoords]);

    const handleMouseUp = useCallback((e) => {
        if (interaction?.type === 'drawing_box') {
            // Finalize the new box
            const { startCoords, currentCoords } = interaction;
            const left = Math.min(startCoords.x, currentCoords.x);
            const top = Math.min(startCoords.y, currentCoords.y);
            const width = Math.abs(startCoords.x - currentCoords.x);
            const height = Math.abs(startCoords.y - currentCoords.y);

            // Only add if it has some dimensions
            if (width > 1 / imgSize.width && height > 1 / imgSize.height) {
                let newId = nextId;
                if(!newId) {
                    newId = prompt('Enter an ID for the new bounding box', generateId());
                }   
                addAnnotation({
                    id: newId,
                    type: 'boundingBox',
                    left, top, width, height
                });
            }
            setInteractionMode('select'); // Back to select mode
        }

        // Clear interaction state for all types
        setInteraction(null);
    }, [interaction, addAnnotation, imgSize?.width, imgSize?.height]);

     // Handle zoom via mouse wheel
    const handleWheel = useCallback((e) => {
        e.preventDefault(); // Prevent page scrolling when zooming
        e.stopPropagation();
        if (!imgSize?.width || !containerRef.current) return;

        const wheelDelta = -e.deltaY * ZOOM_SENSITIVITY;
        const MIN_ZOOM = computeMinZoom();
        const newZoom = clamp(zoom + wheelDelta * zoom, MIN_ZOOM, MAX_ZOOM); // Make zoom exponential

        if (newZoom === zoom && newZoom === MIN_ZOOM) {
            resetZoom();
            return;
        };

        const contRect = containerRef.current.getBoundingClientRect();
        const mouseX = e.clientX - contRect.left; // Mouse pos relative to container
        const mouseY = e.clientY - contRect.top;

        // Calculate the point under the mouse in the *unscaled* image coordinate system
        // (mouseX - pan.x) / zoom = imageX
        const imageX_before = (mouseX - pan.x) / zoom;
        const imageY_before = (mouseY - pan.y) / zoom;

        // Calculate the new pan value such that the point under the mouse remains the same
        // mouseX = newPanX + imageX_before * newZoom
        const newPanX = mouseX - imageX_before * newZoom;
        const newPanY = mouseY - imageY_before * newZoom;

        setZoom(newZoom);
        setPan({ x: newPanX, y: newPanY });

    }, [zoom, imgSize, pan, containerSize]);

    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;
   
        // Use the memoized handleWheel directly
        const wheelHandler = (event) => {
            handleWheel(event); // Call your existing logic
        };
   
        // Add listener explicitly marked as non-passive
        container.addEventListener('wheel', wheelHandler, { passive: false });
   
        // Cleanup function to remove the listener
        return () => {
            container.removeEventListener('wheel', wheelHandler, { passive: false });
        };
    }, [handleWheel]); // Dependency array includes handleWheel

     // Handle keyboard navigation for selected annotation
    const handleKeyDown = useCallback((e) => {
        if (interactionMode !== 'select' || !selectedAnnotationId || !imgSize.width || interaction) return;

        const ann = annotations.find(a => a.id === selectedAnnotationId);
        if (!ann) return;

        // Calculate 1 pixel movement in relative coordinates
        const dxRel = 1 / imgSize.width;
        const dyRel = 1 / imgSize.height;

        let changes = {};
        let moved = false;

        switch (e.key) {
            case 'ArrowUp':
                 if (ann.type === 'point') changes = { y: ann.y - dyRel };
                 if (ann.type === 'boundingBox') changes = { top: ann.top - dyRel };
                 moved = true;
                 break;
            case 'ArrowDown':
                 if (ann.type === 'point') changes = { y: ann.y + dyRel };
                 if (ann.type === 'boundingBox') changes = { top: ann.top + dyRel };
                 moved = true;
                 break;
            case 'ArrowLeft':
                 if (ann.type === 'point') changes = { x: ann.x - dxRel };
                 if (ann.type === 'boundingBox') changes = { left: ann.left - dxRel };
                 moved = true;
                 break;
            case 'ArrowRight':
                 if (ann.type === 'point') changes = { x: ann.x + dxRel };
                 if (ann.type === 'boundingBox') changes = { left: ann.left + dxRel };
                 moved = true;
                 break;
            case 'Delete':
            case 'Backspace':
                deleteAnnotation(selectedAnnotationId);
                moved = true; // Prevent default browser back navigation on Backspace
                break;
            default:
                break;
        }

        if (moved) {
            e.preventDefault();
            if (Object.keys(changes).length > 0) {
                 updateAnnotation(selectedAnnotationId, changes);
            }
        }
    }, [selectedAnnotationId, annotations, imgSize, updateAnnotation, interaction, deleteAnnotation, interactionMode]);

    // Attach/Detach global mouse move/up listeners during drag/pan/resize
    useEffect(() => {
        const handleGlobalMouseMove = (e) => handleMouseMove(e);
        const handleGlobalMouseUp = (e) => handleMouseUp(e);

        if (interaction) {
            window.addEventListener('mousemove', handleGlobalMouseMove);
            window.addEventListener('mouseup', handleGlobalMouseUp);
        }

        return () => {
            window.removeEventListener('mousemove', handleGlobalMouseMove);
            window.removeEventListener('mouseup', handleGlobalMouseUp);
        };
    }, [interaction, handleMouseMove, handleMouseUp]);

    // --- Button Actions ---
    const handleAddPoint = (id) => {
        setInteractionMode('add_point');
        setNextId(id);
    };
    const handleAddBox = (id) => {
        setInteractionMode('add_box');
        setNextId(id);
    };
  

    // --- Rendering ---

    // Render bounding box handles with constant visual size
    const renderHandles = (ann) => {
        if (!imgSize.width) return null;

        // Calculate handle size in SVG coordinates (needs to be scaled down as zoom increases)
        const handleSizeSvg = BASE_HANDLE_SIZE / zoom;
        const halfHandleSvg = handleSizeSvg / 2;

        // Box position and dimensions in SVG coordinates
        const boxSvg = relativeToSvgCoords(ann.left, ann.top);
        const w = ann.width * imgSize.width;
        const h = ann.height * imgSize.height;

        const x = boxSvg.x;
        const y = boxSvg.y;

        const handlesData = [
            // First laterals
            { type: 't', x1: x, x2: x + w, y1: y, y2: y, line: true  }, 
            { type: 'b', x1: x, x2: x + w, y1: y + h, y2: y + h, line: true },
            { type: 'l', x1: x, x2: x, y1: y, y2: y + h, line: true }, 
            { type: 'r', x1: x + w, x2: x + w, y1: y, y2: y + h, line: true },

            // Then corners so they are drawn on top of the laterals
            { type: 'tl', x, y }, 
            { type: 'tr', x: x + w, y },
            { type: 'bl', x, y: y + h }, 
            { type: 'br', x: x + w, y: y + h },
        ];

        const cursorMap = { tl: 'nwse-resize', tr: 'nesw-resize', bl: 'nesw-resize', br: 'nwse-resize', t: 'ns-resize', b: 'ns-resize', l: 'ew-resize', r: 'ew-resize' };

        return _.compact(_.flatMap(handlesData, h => [
            h.line && <line 
            key={`${h.type}-line`}
            data-annotation-id={ann.id}
            data-handle-type={h.type}
            className=""
            style={{ cursor: cursorMap[h.type], strokeWidth: 10*Math.max(1, BASE_STROKE_WIDTH / zoom) }}
            x1={h.x1}
            y1={h.y1}
            x2={h.x2}
            y2={h.y2}
            stroke="transparent"
        />,
        h.line && <line 
            key={`${h.type}-line2`}
            data-annotation-id={ann.id}
            data-handle-type={h.type}
            className="annotation-handle-line"
            style={{ cursor: cursorMap[h.type], strokeWidth: Math.max(1, BASE_STROKE_WIDTH / zoom) }}
            x1={h.x1}
            y1={h.y1}
            x2={h.x2}
            y2={h.y2}
            stroke="rgba(255, 255, 0, 0.1)"
        />,
            <rect
                key={h.type}
                data-annotation-id={ann.id}
                data-handle-type={h.type}
                // Position the handle center at the calculated point (x, y)
                x={(h.line ? (h.x1+h.x2)/2 : h.x) - halfHandleSvg}
                y={(h.line ? (h.y1+h.y2)/2 : h.y) - halfHandleSvg}
                width={handleSizeSvg}
                height={handleSizeSvg}
                className="annotation-handle"
                style={{ cursor: cursorMap[h.type], strokeWidth: BASE_STROKE_WIDTH / zoom }}
            />,
         
        ]));
    };

    // Render the temporary box being drawn
    const renderDrawingBox = () => {
        if (interaction?.type !== 'drawing_box') return null;
        const { startCoords, currentCoords } = interaction;
        const startSvg = relativeToSvgCoords(startCoords.x, startCoords.y);
        const currentSvg = relativeToSvgCoords(currentCoords.x, currentCoords.y);

        const left = Math.min(startSvg.x, currentSvg.x);
        const top = Math.min(startSvg.y, currentSvg.y);
        const width = Math.abs(startSvg.x - currentSvg.x);
        const height = Math.abs(startSvg.y - currentSvg.y);

        return (
             <rect
                x={left}
                y={top}
                width={width}
                height={height}
                className="drawing-box" // Add specific styling
                strokeDasharray="4"
                stroke="cyan"
                fill="rgba(0, 255, 255, 0.2)"
                strokeWidth={BASE_STROKE_WIDTH / zoom}
            />
        )
    };


     // Determine cursor based on mode and interaction
    const getCursor = () => {
        if (interaction?.type === 'pan') return 'grabbing';
        if (interactionMode === 'add_point') return 'crosshair';
        if (interactionMode === 'add_box') return 'crosshair';
        if (interaction?.type?.startsWith('drag_') || interaction?.type?.startsWith('resize_')) return 'grabbing'; // Or specific resize cursors if possible
        if (zoom > 1 && interactionMode === 'select' && !interaction) return 'grab';
        return 'default';
    }

    // Sort annotations by type and rectangle area, first circle, then rectangles from small to large
    const sortedAnnotations = _.sortBy(annotations, (ann) => {
        if (ann.type === 'point') return 0;
        if (ann.type === 'boundingBox') return -ann.width * ann.height;
        return 1;
    });

    const pixelWidth = 1 / zoom;

    return (
        <div className="picture-annotations-editor-container">
             {/* Toolbar */}
            <div className="d-flex p-1 justify-content-between align-items-center">
                <span>
                    <IconButton
                        icon="my_location"
                        onClick={() => handleAddPoint()}
                        description="Add Point"
                        level={interactionMode === 'add_point' ? 'success' : 'primary'} // Highlight if active
                        fill={interactionMode === 'add_point' && !nextId}
                    />
                    <IconButton
                        icon="highlight_alt"
                        onClick={() => handleAddBox()}
                        description="Add Bounding Box"
                        level={interactionMode === 'add_box' ? 'success' : 'primary'}
                        fill={interactionMode === 'add_box' && !nextId}
                    />
                    <IconButton
                        icon="zoom_out_map"
                        onClick={resetZoom}
                        description="Reset Zoom (100%)"
                        level="secondary"
                    />
                </span>

                <div className="picture-annotations-toolbar-annotations zoom-90 ml-4">
                { _.map(knownAnnotations, ({id, type, color}) => {
                    if(annotations.find(a => a.id === id)){
                        return  <IconButton
                        icon={"task_alt"}
                        key={id+'add-point'}
                        onClick={() => setSelectedAnnotationId(id)}
                        description="Add Point"
                        level={'success'}
                        className='bg-light-success'
                        style={{borderColor: color, backgroundColor: color+'10'}}
                        fill={false}
                    >
                            <span style={{color: color}}>{id}</span>
                        </IconButton>
                    } else if(type === 'point') {
                    return <IconButton
                        icon="add"
                        key={id+'edit-point'}
                        onClick={() => handleAddPoint(id)}
                        description={"Add "+id}
                        level={interactionMode === 'add_point' && nextId === id ? 'success' : 'primary'} // Highlight if active
                        fill={interactionMode === 'add_point' && nextId === id}
                    >
                            {id}
                        </IconButton>
                    } else if(type === 'boundingBox') {
                        return <IconButton
                            icon="add"
                            key={id+'add-box'}
                            onClick={() => handleAddBox(id)}
                            description={"Add "+id}
                            level={interactionMode === 'add_box' && nextId === id ? 'success' : 'primary'} // Highlight if active
                            fill={interactionMode === 'add_box' && nextId === id}
                        >
                            {id}
                        </IconButton>
                    }
                })}
                </div>

                <div className='image-size-pixels small monospace'>
                    {imgSize ? `${imgSize.width} x ${imgSize.height}` : ''}
                    <IconButton icon={'open_in_new'} href={url} target='_blank' level={'primary'} className='ml-2 zoom-75 align-middle'/>
                </div>
            </div>

            {/* Editor Area */}
            <div
                ref={containerRef}
                className="picture-annotations-editor"
                style={{
                    cursor: getCursor(), // Dynamic cursor
                    overflow: 'hidden', // Clip content
                    position: 'relative', // For positioning the wrapper
                }}
                // onWheel={handleWheel}
                onKeyDown={handleKeyDown}
                onMouseDown={handleMouseDown} // Attach mouse down here
                tabIndex={0}
            >
                <div
                    ref={transformWrapperRef}
                    className="transform-wrapper"
                    style={{
                        // Apply pan and zoom transform
                        transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
                        transformOrigin: '0 0', // Scale from top-left
                        width: imgSize?.width, // Set explicit size for SVG mapping
                        height: imgSize?.height,
                        position: 'absolute', // Allow positioning via transform
                        willChange: 'transform', // Hint for performance            
                    }}
                >
                    <img
                        ref={imgRef} // Ref is now managed in effect
                        src={url} // src managed by effect Image object
                        alt="Annotatable content"
                        crossOrigin="anonymous"
                        style={{
                            display: 'block',
                            width: '100%', // Fill the wrapper
                            height: '100%',
                            userSelect: 'none',
                            // @ts-ignore
                            WebkitUserDrag: 'none',
                            imageRendering: 'pixelated', // Crisp pixels
                            pointerEvents: 'none',
                            outlineWidth: 1*pixelWidth+'px',
                        }}
                        draggable="false"
                        onLoad={handleLoad}
                    />                                        

                    <svg
                        className="annotation-overlay"
                        style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%', // Match wrapper size
                            height: '100%',
                            overflow: 'visible', // Allow handles slightly outside box
                            // pointerEvents: 'none' // Let wrapper handle mouse down initially
                        }}
                            // ViewBox is now effectively 0 0 imgSize.width imgSize.height due to wrapper scaling
                        // viewBox={`0 0 ${imgSize.width} ${imgSize.height}`} // No longer needed
                        // preserveAspectRatio="none" // No longer needed with CSS transform approach
                    >
                        <g> {/* Group for all annotations */}
                            {imgSize &&sortedAnnotations.map(ann => {
                                const isSelected = ann.id === selectedAnnotationId;
                                const classNames = `annotation ${isSelected ? 'selected' : ''}`;
                                const strokeWidth = BASE_STROKE_WIDTH / zoom; // Scale stroke width
                        
                                let color = knownAnnotations.find(a => a.id === ann.id)?.color || '#FF0000';

                                if (ann.type === 'point' && ann.x != null && ann.y != null) {
                                    const pointSvg = relativeToSvgCoords(ann.x, ann.y);
                                    const radiusSvg = BASE_POINT_RADIUS / zoom;

                                    
                                    return (
                                        <g
                                            key={ann.id}
                                            className={`${classNames} annotation-point`}
                                            style={{ cursor: interactionMode === 'select' ? 'move' : 'default' }}
                                                // Apply transform to counter-scale AND center the scaling
                                            transform={`translate(${pointSvg.x}, ${pointSvg.y}) scale(${1 / zoom}) translate(${-pointSvg.x}, ${-pointSvg.y})`}
                                            data-annotation-id={ann.id} // Put ID on group for easier targeting
                                            >
                                                {/* Invisible larger circle for easier selection */}
                                            <circle
                                                cx={pointSvg.x} // Center relative to the non-transformed group
                                                cy={pointSvg.y}
                                                data-annotation-id={ann.id}
                                                r={BASE_POINT_RADIUS * 2} // Base radius * multiplier
                                                fill="transparent"
                                                // pointerEvents="visible" // Let this capture events
                                            />
                                                {/* Visible point - uses base radius */}
                                            <circle
                                                cx={pointSvg.x}
                                                cy={pointSvg.y}
                                                r={BASE_POINT_RADIUS}
                                                fill={isSelected ? "yellow" : color}
                                                stroke="white"
                                                strokeWidth={BASE_STROKE_WIDTH * 1.2} // Slightly thicker base stroke
                                                style={{ pointerEvents: 'none' }} // Visual only
                                            />
                                            <text data-annotation-id={ann.id} x={pointSvg.x+7} y={pointSvg.y-7} fill={isSelected ? "yellow" : color} fontSize={12} fontFamily="Arial" textAnchor="left" dominantBaseline="">{ann.id}</text>
                                        </g>
                                    );
                                } else if (ann.type === 'boundingBox' && ann.left != null && ann.top != null && ann.width != null && ann.height != null) {
                                    const boxSvg = relativeToSvgCoords(ann.left, ann.top);
                                    const boxW = ann.width * imgSize.width;
                                    const boxH = ann.height * imgSize.height;

                                    return (
                                        <g key={ann.id} className={`${classNames} annotation-box`} >
                                            <rect
                                                data-annotation-id={ann.id}
                                                x={boxSvg.x}
                                                y={boxSvg.y}
                                                width={boxW}
                                                height={boxH}
                                                fill={isSelected ? "rgba(255, 255, 0, 0.1)" : color+"20"}
                                                stroke={isSelected ? "rgba(255, 255, 0, 0.5)" : color}
                                                strokeWidth={strokeWidth} // Scaled stroke width
                                                style={{ cursor: interactionMode === 'select' ? 'move' : 'default', zIndex: isSelected ? -1 : 10 }}
                                            />
                                            <text data-annotation-id={ann.id} x={boxSvg.x+5} y={boxSvg.y-5} fill={color} fontSize={16} fontFamily="Arial" textAnchor="left" dominantBaseline="">{ann.id}</text>
                                            {/* Handles are rendered separately and apply their own scaling */}
                                            {isSelected && interactionMode === 'select' && renderHandles(ann)}
                                        </g>
                                    );
                                }
                                return null;
                            })}
                            {/* Render temporary drawing box */}
                            {renderDrawingBox()}
                        </g>
                    </svg>
                </div>            
            </div>
        </div>
    );
}

export default ImageAnnotationsEditor;